import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { studyGroupService } from '../../services/study-groups';
import type { StudyGroup, StudySession, SharedNote } from '../../lib/db';
import {
  Card,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  TextField,
  TextArea,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Avatar,
  AvatarImage,
  AvatarFallback
} from '../ui';

export function StudyGroupDetail() {
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuth();
  const [group, setGroup] = useState<StudyGroup>();
  const [sessions, setSessions] = useState<StudySession[]>([]);
  const [sharedNotes, setSharedNotes] = useState<SharedNote[]>([]);
  const [createSessionOpen, setCreateSessionOpen] = useState(false);
  const [newSession, setNewSession] = useState({
    name: '',
    description: '',
    scheduledFor: '',
    duration: 60,
    verses: [] as string[]
  });

  useEffect(() => {
    if (groupId) {
      loadGroupData();
    }
  }, [groupId]);

  async function loadGroupData() {
    if (!groupId) return;
    const id = parseInt(groupId);
    
    const [groupData, sessionsData, notesData] = await Promise.all([
      studyGroupService.getGroup(id),
      studyGroupService.getGroupSessions(id),
      studyGroupService.getGroupNotes(id)
    ]);

    setGroup(groupData);
    setSessions(sessionsData);
    setSharedNotes(notesData);
  }

  async function handleCreateSession(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !group?.id) return;

    try {
      await studyGroupService.createSession({
        ...newSession,
        groupId: group.id,
        createdBy: user.id,
        participants: [user.id],
        notes: ''
      });
      setCreateSessionOpen(false);
      setNewSession({
        name: '',
        description: '',
        scheduledFor: '',
        duration: 60,
        verses: []
      });
      loadGroupData();
    } catch (error) {
      console.error('Failed to create session:', error);
      // TODO: Show error toast
    }
  }

  async function handleShareNote(note: SharedNote) {
    if (!user || !group?.id) return;

    try {
      await studyGroupService.shareNote({
        ...note,
        groupId: group.id,
        sharedBy: user.id
      });
      loadGroupData();
    } catch (error) {
      console.error('Failed to share note:', error);
      // TODO: Show error toast
    }
  }

  if (!group) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-4">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">{group.name}</h1>
            <p className="text-gray-600 mt-2">{group.description}</p>
          </div>
          <Button variant="outline">Invite Members</Button>
        </div>

        <div className="mt-4 flex items-center space-x-2">
          <h3 className="font-semibold">Members:</h3>
          <div className="flex -space-x-2">
            {group.members.map((memberId) => (
              <Avatar key={memberId} className="border-2 border-white">
                <AvatarFallback>{memberId.slice(0, 2)}</AvatarFallback>
              </Avatar>
            ))}
          </div>
        </div>
      </div>

      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions">Study Sessions</TabsTrigger>
          <TabsTrigger value="notes">Shared Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="sessions">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Study Sessions</h2>
            <Dialog open={createSessionOpen} onOpenChange={setCreateSessionOpen}>
              <DialogTrigger asChild>
                <Button>Schedule Session</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogTitle>Schedule Study Session</DialogTitle>
                <DialogDescription>
                  Schedule a new study session for your group.
                </DialogDescription>
                <form onSubmit={handleCreateSession} className="space-y-4">
                  <TextField
                    label="Session Name"
                    value={newSession.name}
                    onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                    required
                  />
                  <TextArea
                    label="Description"
                    value={newSession.description}
                    onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                    required
                  />
                  <TextField
                    type="datetime-local"
                    label="Date and Time"
                    value={newSession.scheduledFor}
                    onChange={(e) => setNewSession({ ...newSession, scheduledFor: e.target.value })}
                    required
                  />
                  <TextField
                    type="number"
                    label="Duration (minutes)"
                    value={newSession.duration.toString()}
                    onChange={(e) => setNewSession({ ...newSession, duration: parseInt(e.target.value) })}
                    required
                  />
                  <Button type="submit">Create Session</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {sessions.map((session) => (
              <Card key={session.id} className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{session.name}</h3>
                    <p className="text-gray-600 mt-1">{session.description}</p>
                  </div>
                  <Button variant="outline">Join Session</Button>
                </div>
                <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500">
                  <span>
                    {new Date(session.scheduledFor).toLocaleDateString()} at{' '}
                    {new Date(session.scheduledFor).toLocaleTimeString()}
                  </span>
                  <span>{session.duration} minutes</span>
                  <span>{session.participants.length} participants</span>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notes">
          <div className="space-y-4">
            {sharedNotes.map((note) => (
              <Card key={note.id} className="p-4">
                <div className="flex items-start space-x-4">
                  <Avatar>
                    <AvatarFallback>{note.sharedBy.slice(0, 2)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-semibold">{note.sharedBy}</span>
                        <span className="text-gray-500 text-sm ml-2">
                          {new Date(note.sharedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <p className="mt-2">{note.content}</p>
                    <div className="mt-4 space-x-2">
                      {Object.entries(note.reactions).map(([userId, reaction]) => (
                        <span key={userId} className="inline-flex items-center space-x-1">
                          <span>{reaction}</span>
                          <span className="text-xs text-gray-500">{userId.slice(0, 2)}</span>
                        </span>
                      ))}
                    </div>
                    {note.comments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {note.comments.map((comment) => (
                          <div key={comment.id} className="flex items-start space-x-2">
                            <Avatar className="w-6 h-6">
                              <AvatarFallback>{comment.userId.slice(0, 2)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <span className="font-semibold text-sm">{comment.userId}</span>
                              <p className="text-sm">{comment.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
